import { Notification } from '@/features/Notifications/Notifications.interfaces';
import { useLog } from '@/features/shared/Hooks/useLog';
import {
  getPortalNotificationsService,
  readPortalNotificationsService,
} from '@/services/PortalNotifications.services';
import { getOr, pipe } from 'lodash/fp';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useContact } from '../Contact';
import { usePageModels } from '../PageModels';
import {
  getLength,
  getUnreadNotificationIds,
  getUnreadNotifications,
  readAllNotifications,
  readSingleNotification,
} from './PortalNotifications.utils';
import { useNotificationsEnabled } from './usePortalNotifications';

enum NotificationConfigKeys {
  mainKey = 'portalNotifications',
  syncNotificationsTimeIntervalMs = 'sync_notifications_time_interval_ms',
}

interface PortalNotificationsContextProps {
  syncNotifications: any;
  markAllAsRead: any;
  markAsRead: any;
  notifications: Notification[] | null;
  unreadNotificationCount?: number;
}

const PortalNotificationsContext = createContext<PortalNotificationsContextProps>({
  syncNotifications: () => Promise.resolve([]),
  markAllAsRead: () => Promise.resolve([]),
  markAsRead: () => Promise.resolve([]),
  notifications: [],
  unreadNotificationCount: undefined,
});

export const PortalNotificationsProvider = ({ children }: React.PropsWithChildren<any>) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isNotificationsLoading, setIsNotificationsLoading] = useState(true);
  const timerRef = useRef<any>(null);
  const firstLoad = useRef(true);
  const log = useLog({ context: 'PortalNotifications' });
  const { config } = usePageModels();
  const contact = useContact();

  const notificationAvailabilityStatus = useNotificationsEnabled();

  const unreadNotificationCount = useMemo(() => {
    if (isNotificationsLoading) return undefined;

    // executing functions in pipe to get the unread count},
    return pipe(getUnreadNotifications, getLength)(notifications);
  }, [notifications, isNotificationsLoading]);

  const notificationConfig = useMemo(
    () => getOr({}, NotificationConfigKeys.mainKey, config),
    [config],
  );

  const syncNotificationTimeIntervalMS = useMemo(() => {
    const intervalConfig =
      notificationConfig[NotificationConfigKeys.syncNotificationsTimeIntervalMs] || 7200000; // 5min
    return parseFloat(intervalConfig);
  }, [notificationConfig]);

  const markAllAsRead = useCallback(async () => {
    try {
      const unreadNotificationIds = getUnreadNotificationIds(notifications);
      setIsNotificationsLoading(true);
      await readPortalNotificationsService(unreadNotificationIds);
      setNotifications(readAllNotifications(notifications));
      setIsNotificationsLoading(false);
    } catch (e) {
      console.error('An error occurred marking as read all the notifications.', e);
    }
  }, [notifications]);

  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        setIsNotificationsLoading(true);
        await readPortalNotificationsService([notificationId]);
        setNotifications(readSingleNotification(notificationId, notifications));
        setIsNotificationsLoading(false);
      } catch (e) {
        console.error('An error occurred marking as read a notification', e);
      }
    },
    [setNotifications, notifications],
  );

  const syncNotifications = useCallback(async () => {
    try {
      log('Getting portal notifications');
      setIsNotificationsLoading(true);
      const { notifications: notificationList } = await getPortalNotificationsService(
        contact?.contactInfo?.portalNotificationSettings || undefined,
      );
      setNotifications(notificationList);
      setIsNotificationsLoading(false);
    } catch (e) {
      console.error('There was an error getting the user notifications');
    }
  }, [log, contact?.contactInfo?.portalNotificationSettings]);

  const getPortalNotifications = useCallback(() => {
    if (!notificationAvailabilityStatus.enabled) {
      clearInterval(timerRef.current);
      return;
    }
    syncNotifications();
  }, [notificationAvailabilityStatus, syncNotifications]);

  useEffect(() => {
    if (notificationAvailabilityStatus.enabled) {
      if (firstLoad.current) {
        getPortalNotifications();
        firstLoad.current = false;
      }
      const intervalTask = () => {
        getPortalNotifications();
        log('Syncing notifications by interval', syncNotificationTimeIntervalMS);
      };

      timerRef.current = setInterval(intervalTask, syncNotificationTimeIntervalMS);
    } else {
      clearInterval(timerRef.current);
    }
  }, [notificationAvailabilityStatus, getPortalNotifications, log, syncNotificationTimeIntervalMS]);

  return (
    <PortalNotificationsContext.Provider
      value={{
        syncNotifications,
        markAllAsRead,
        markAsRead,
        notifications,
        unreadNotificationCount,
      }}
    >
      {children}
    </PortalNotificationsContext.Provider>
  );
};

export const PreviewPortalNotificationsProvider = ({ children }: React.PropsWithChildren<any>) => {
  return (
    <PortalNotificationsContext.Provider
      value={{
        syncNotifications: () => Promise.resolve([]),
        markAllAsRead: Promise.resolve(),
        markAsRead: Promise.resolve(),
        notifications: [],
        unreadNotificationCount: 0,
      }}
    >
      {children}
    </PortalNotificationsContext.Provider>
  );
};

export const usePortalNotifications = () => {
  const notificationContext = useContext(PortalNotificationsContext);

  if (!notificationContext) {
    throw new Error('You need the NotificationsProvider');
  }

  return notificationContext;
};
